import { cn } from '@neo/lib/utils'
import { Badge } from '@neo/primitives/badge'
import { m } from 'framer-motion'
import type { GateQuestionOption } from '../wire'

interface QuestionOptionProps {
  option: GateQuestionOption
  index: number
  isSelected: boolean
  isFocused: boolean
  countdownArmed: boolean
  countdown: number
  onSelect: () => void
  onFocus: () => void
}

// Stagger child of the question block in question-card: the parent drives
// hidden→visible with staggerChildren, each row settles with a soft spring.
const optionEnter = {
  hidden: { opacity: 0, transform: 'translateY(6px)' },
  visible: {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: { type: 'spring' as const, duration: 0.3, bounce: 0.15 },
  },
}

export const QuestionOption = ({
  option,
  index,
  isSelected,
  isFocused,
  countdownArmed,
  countdown,
  onSelect,
  onFocus,
}: QuestionOptionProps) => {
  let stateClass = 'border-st2 hover:border-primary/50 bg-bg1'
  if (isSelected) {
    stateClass = 'border-primary bg-primary/10'
  } else if (isFocused) {
    stateClass = 'border-primary/50 bg-bg1'
  }

  return (
    <m.button
      type="button"
      variants={optionEnter}
      whileTap={{ scale: 0.98 }}
      aria-pressed={isSelected}
      onClick={onSelect}
      onMouseEnter={onFocus}
      className={cn(
        'w-full p-3 rounded-lg border text-left transition-[border-color,background-color] duration-150',
        stateClass,
      )}
      data-testid={`button-question-option-${index}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn('text-p-500', isSelected ? 'text-primary' : 'text-secondary')}>
          <span className="text-xs-500 text-tertiary tabular-nums mr-2" aria-hidden="true">
            {index + 1}
          </span>
          {option.label}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {option.recommended && !countdownArmed && (
            <Badge variant="tag" className="whitespace-nowrap" data-testid="badge-recommended">
              Recommended
            </Badge>
          )}
          {option.recommended && countdownArmed && (
            <span
              className="text-xs-500 text-tertiary whitespace-nowrap"
              data-testid="badge-recommended-countdown"
            >
              {countdown > 0 ? (
                <>
                  Auto-selecting in{' '}
                  <span className="font-jetbrains tabular-nums">{countdown}s</span>
                </>
              ) : (
                'Auto-selecting…'
              )}
            </span>
          )}
          {isSelected && (
            <span
              aria-hidden="true"
              className="w-2.5 h-2.5 rounded-full bg-primary shrink-0"
              data-testid="indicator-option-selected"
            />
          )}
        </div>
      </div>
      {option.description && (
        <p className="text-xs-400 text-secondary mt-1">{option.description}</p>
      )}
    </m.button>
  )
}
